import type { ReactElement } from 'react';
import { useCallback, useContext } from 'react';
import { Home } from 'react-feather';
import styled from '@emotion/styled';
import { BrandInsightChatContexts, ChatContextType } from '@trinity/chat';
import {
  CapabilitiesContext,
  ChatContextSelectorVisibleContext,
  ChatHomeButtonVisibleContext,
  ChatViewType,
  ChatViewTypeContext,
  LinkContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import {
  ActiveChatSessionContext,
  ChatLandingCardsVisibleContext,
} from '@trinity/chat-components/components/Chat/context';
import { FilterButton } from '@trinity/chat-components/components/Chat.Filters';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { ChatAppPlatformContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { GenAIExternalPlatform } from '@trinity/management-app-client';
import { useIsFullPageChat } from '../Chat/hooks/useIsFullPageChat';
import { useAgentResources } from './AgentResources.Container';
import { ChatTabs } from './Chat.Tabs';
import { ContextSubjectDisplay } from './ContextSubjectDisplay';
import { ChatHeaderPresentation } from './Header.Presentation';
import { SimpleContextSelector } from './SimpleContextSelector';
import { SourceLink } from './SourceLink';

type Props = {
  className?: string;
  disableTabs?: boolean;
  disableContextDisplay?: boolean;
};

function ChatHeaderController({ className, disableContextDisplay, disableTabs }: Props): ReactElement {
  const link = useContext(LinkContext);
  const capabilities = useContext(CapabilitiesContext);
  const session = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();
  const navigate = useNavigate();
  const landingCardsVisible = useContext(ChatLandingCardsVisibleContext);
  const isFullPageChat = useIsFullPageChat();
  const viewMode = useContext(ChatViewTypeContext);
  const platform = useContext(ChatAppPlatformContext);
  const homeButtonVisible = useContext(ChatHomeButtonVisibleContext);
  const contextSelectorVisible = useContext(ChatContextSelectorVisibleContext);
  const agentResources = useAgentResources();
  const { isDigitalTwin, interactionMode } = useAvatarPanel();

  const handleHomeClick = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  const visibility = computeHeaderVisibility({
    isDigitalTwin,
    interactionMode,
    agentResources,
    link,
    viewMode,
    disableTabs,
    homeButtonVisible,
    features,
    isFullPageChat,
    platform,
    landingCardsVisible,
    disableContextDisplay,
    capabilities,
    session,
  });

  return (
    <ChatHeaderPresentation
      className={className}
      isLoading={visibility.isLoading}
      shrinkToContent={visibility.isDTTextVoice}
      navigation={
        visibility.showHomeButton && (
          <Tooltip title="Home">
            <StyledHomeButton onClick={handleHomeClick}>
              <StyledHomeIcon size={20} />
            </StyledHomeButton>
          </Tooltip>
        )
      }
      filterAction={visibility.showFilterButton && <FilterButton />}
      contextSelector={visibility.showContextSelector && <SimpleContextSelector disabled={!contextSelectorVisible} />}
      contextDisplay={visibility.showContextDisplay && <ContextSubjectDisplay />}
      inlineEndAction={visibility.showInlineEndAction && <SourceLink />}
      tabs={visibility.showTabs && <ChatTabs />}
      endAction={visibility.showEndAction && <SourceLink />}
    />
  );
}

function computeHeaderVisibility({
  isDigitalTwin,
  interactionMode,
  agentResources,
  link,
  viewMode,
  disableTabs,
  homeButtonVisible,
  features,
  isFullPageChat,
  platform,
  landingCardsVisible,
  disableContextDisplay,
  capabilities,
  session,
}: {
  isDigitalTwin: boolean;
  interactionMode: string;
  agentResources: ReturnType<typeof useAgentResources>;
  link: { initialized: boolean };
  viewMode: ChatViewType;
  disableTabs: boolean | undefined;
  homeButtonVisible: boolean;
  features: ReturnType<typeof useGroupFeatures>;
  isFullPageChat: boolean;
  platform: GenAIExternalPlatform;
  landingCardsVisible: boolean;
  disableContextDisplay: boolean | undefined;
  capabilities: { canViewExternalLinks: boolean };
  session: { context: { type: string; data?: Record<string, unknown> } };
}) {
  const isDTTextVoice = isDigitalTwin && interactionMode !== 'video';
  const isDTVideo = isDigitalTwin && interactionMode === 'video';
  const isLoading = agentResources.isLoading && !link.initialized;

  const showTabs = viewMode === ChatViewType.Page && !disableTabs;

  const showHomeButton =
    homeButtonVisible &&
    features.homeButton &&
    isFullPageChat &&
    platform === GenAIExternalPlatform.GenAIPlatform;

  const showContextSelector = !isDTVideo && !(features.chatContextSelector && landingCardsVisible);
  const showContextDisplay = disableContextDisplay !== true && !isDTVideo;

  const showFilterButton = computeShowFilterButton(session, agentResources);

  const showInlineEndAction = isDTTextVoice && capabilities.canViewExternalLinks;
  const showEndAction = !isDTTextVoice && !isDTVideo && capabilities.canViewExternalLinks;

  return { isDTTextVoice, isLoading, showTabs, showHomeButton, showContextSelector, showContextDisplay, showFilterButton, showInlineEndAction, showEndAction };
}

function computeShowFilterButton(session: { context: { type: string; data?: Record<string, unknown> } }, agentResources: ReturnType<typeof useAgentResources>): boolean {
  if (session.context.type === ChatContextType.Agent) {
    if (BrandInsightChatContexts.includes(session.context.data?.identifier as string)) {
      return true;
    }
    return agentResources.data.filters.length > 0 || agentResources.data.dataSources.length > 0;
  }
  return true;
}

export { ChatHeaderController };

const StyledHomeButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));

const StyledHomeIcon = styled(Home)(({ theme }) => ({
  color: theme.palette.primary.main,
}));
